import path from 'node:path';
import { promises as fs } from 'node:fs';
import type { SourceInfo } from './types';

/**
 * Find all controller files in a directory
 */
export async function findControllerFiles(dir: string): Promise<string[]> {
  const files: string[] = [];

  async function scan(currentDir: string): Promise<void> {
    const entries = await fs.readdir(currentDir, { withFileTypes: true });

    for (const entry of entries) {
      const fullPath = path.join(currentDir, entry.name);

      if (entry.isDirectory()) {
        await scan(fullPath);
      } else if (entry.isFile() && entry.name.endsWith('.controller.ts')) {
        files.push(fullPath);
      }
    }
  }

  await scan(dir);
  return files;
}

/**
 * Build source map from controller files
 */
export async function buildSourceMap(
  controllerFiles: string[],
  processFile: (filePath: string) => Promise<Map<string, SourceInfo>>,
): Promise<Map<string, SourceInfo>> {
  const sourceMap = new Map<string, SourceInfo>();

  // Process files in parallel with a concurrency limit
  const concurrency = 10;
  const results: Map<string, SourceInfo>[] = [];

  for (let i = 0; i < controllerFiles.length; i += concurrency) {
    const batch = controllerFiles.slice(i, i + concurrency);
    const batchResults = await Promise.all(batch.map((filePath) => processFile(filePath)));
    results.push(...batchResults);
  }

  // Merge results
  for (const metadata of results) {
    for (const [operationId, info] of metadata.entries()) {
      sourceMap.set(operationId, info);
    }
  }

  return sourceMap;
}

/**
 * Try to match operationId with different formats
 * Supports:
 * - Direct match: ClassName_methodName
 * - Camel case: classNameMethodName
 * - Method only: methodName
 */
function findSourceInfo(operationId: string, sourceMap: Map<string, SourceInfo>): SourceInfo | undefined {
  // Try direct match first
  const directMatch = sourceMap.get(operationId);
  if (directMatch) {
    return directMatch;
  }

  // Try matching with different formats
  for (const [key, value] of sourceMap.entries()) {
    // key format: ClassName_methodName
    const [className, methodName] = key.split('_');
    if (!className || !methodName) continue;

    // Try camelCase format: classNameMethodName
    const camelCaseId = className.charAt(0).toLowerCase() + className.slice(1) + methodName.charAt(0).toUpperCase() + methodName.slice(1);
    if (operationId === camelCaseId) {
      return value;
    }

    // Try method name only
    if (operationId === methodName) {
      return value;
    }
  }

  return undefined;
}

/**
 * Enhance OpenAPI paths with source information
 */
export function enhanceOpenApiPaths(openapi: any, sourceMap: Map<string, SourceInfo>): number {
  let enhancedCount = 0;

  if (!openapi.paths) {
    return enhancedCount;
  }

  for (const pathItem of Object.values(openapi.paths)) {
    if (!pathItem || typeof pathItem !== 'object') continue;

    for (const operation of Object.values(pathItem)) {
      if (operation && typeof operation === 'object' && 'operationId' in operation) {
        const sourceInfo = findSourceInfo(operation.operationId as string, sourceMap);
        if (sourceInfo) {
          operation['x-source'] = {
            file: sourceInfo.file,
            line: sourceInfo.line,
          };
          enhancedCount++;
        }
      }
    }
  }

  return enhancedCount;
}

/**
 * Transform OpenAPI paths by removing basePath prefix
 */
export function transformOpenapiPaths(openapi: any, basePath: string): any {
  if (basePath === '/' || !openapi.paths) {
    return openapi;
  }

  const newPaths: any = {};
  Object.keys(openapi.paths).forEach((key) => {
    const staticApiKey = key.startsWith(basePath) ? key.slice(basePath.length) : key;
    newPaths[staticApiKey] = openapi.paths[key];
  });

  return {
    ...openapi,
    paths: newPaths,
    basePath,
  };
}
